/**
 * Converts dashed string into camel-cased string
 * @param {string} str - the string being converted
 * @returns {string} camel-cased string
 */
function camelCaseSelector(str: string): string {
  if (typeof str !== 'string') {
    return str
  }

  return str.replace(/^[#|.]/, '').replace(/-+([a-zA-Z]{1})/g, ($2) => {
    return $2.toUpperCase()
  })
}

/**
 * Capitalizes first character of string
 * @param {string} str - the string being converted
 * @returns {string} string starting with capital letter
 */
function capitalizeFirstLetter(str: string): string {
  if (typeof str !== 'string') {
    return str
  }

  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Get DOM Element Dimensions
 * @param  {Element} el - DOM Element
 * @return {Object}     the dimensions as { width, height, outerWidth, outerHeight, widthWithMargins, heightWithMargins }
 */
function getDimensions(el: Element): object {
  const rect = el.getBoundingClientRect()
  const dimensions = {
    width: rect.width,
    height: rect.height,
    outerWidth: rect.width,
    outerHeight: rect.height,
    widthWithMargins: rect.width,
    heightWithMargins: rect.height,
  }
  return dimensions
}

/**
 * getHiddenDimension - get DOM element dimensions if it is hidden
 * inspired by: https://github.com/jcamenisch/jQuery-Hidden-Dimension
 * @param  {Element} el      DOM Element
 * @param  {string} modified Descriptor of which dimension was modified / set to zero - can be `width` or `height`
 * @return {Object}          the dimensions as { width, height, outerWidth, outerHeight, widthWithMargins, heightWithMargins }
 */
function getHiddenDimensions(
  el: HTMLElement,
  modified: string,
  force = false
): object {
  if (el.offsetWidth > 0 && el.offsetHeight > 0 && !force) {
    return getDimensions(el)
  }

  const visibleContainer =
    el.parentElement && el.parentElement.offsetWidth > 0
      ? el.parentElement
      : el.parentElement &&
      el.parentElement.closest(':not([style*="display: none"])')
  const elementClone = el.cloneNode(true) as HTMLElement
  const containerClone = document.createElement('div')
  containerClone.style.cssText =
    'position: absolute; right: 32000px; left: -32000px; top: -32000px'
  containerClone.appendChild(elementClone)
  visibleContainer?.appendChild(containerClone)

  switch (modified) {
    case 'height':
      elementClone.style.height = 'auto'
      break
    case 'width':
      elementClone.style.width = 'auto'
      break
    case 'max-height':
      elementClone.style.maxHeight = 'none'
      break
    default:
  }

  if (elementClone.hasAttribute('aria-expanded')) {
    elementClone.setAttribute('aria-expanded', 'false')
  }

  const dimensions = getDimensions(elementClone)

  containerClone.remove()

  return dimensions
}

function getExpandedDimensions(el: Element): object {
  const visibleContainer =
    el.parentElement && el.parentElement.offsetWidth > 0
      ? el.parentElement
      : el.parentElement &&
      el.parentElement.closest(':not([style*="display: none"])')
  const elementClone = el.cloneNode(true) as Element
  const containerClone = document.createElement('div')
  containerClone.style.cssText =
    'position: absolute; right: 32000px; left: -32000px; top: -32000px'
  containerClone.appendChild(elementClone)
  visibleContainer?.appendChild(containerClone)

  if (elementClone.hasAttribute('aria-expanded')) {
    elementClone.setAttribute('aria-expanded', 'true')
  }

  const dimensions = getDimensions(elementClone)

  containerClone.remove()

  return dimensions
}

function getUnExpandedDimensions(el: Element): object {
  const visibleContainer =
    el.parentElement && el.parentElement.offsetWidth > 0
      ? el.parentElement
      : el.parentElement &&
      el.parentElement.closest(':not([style*="display: none"])')
  const elementClone = el.cloneNode(true) as Element
  const containerClone = document.createElement('div')
  containerClone.style.cssText =
    'position: absolute; right: 32000px; left: -32000px; top: -32000px'
  containerClone.appendChild(elementClone)
  visibleContainer?.appendChild(containerClone)

  if (elementClone.hasAttribute('aria-expanded')) {
    elementClone.setAttribute('aria-expanded', 'false')
  }

  const dimensions = getDimensions(elementClone)

  containerClone.remove()

  return dimensions
}

/**
 * convert a string with seconds to milliseconds
 * @param {string} str - date-time-string
 * @return {number} milliseconds
 */
function secondsToMS(str: string): number {
  return parseFloat(str.replace(/[^0-9\.]/, '')) * 1000
}

/**
 * Get qualified URL
 * @param {string} url - URL string
 * @return {string} qualified URL
 */
function qualifyURL(url: string): string {
  const a = document.createElement('a')
  a.href = url
  return a.href
}

/**
 * Check if given element is an HTML element
 * @param {object} el - Element to check
 * @returns {boolean} - true if it is an HTML element
 */
function isHTMLElement(el: any): boolean {
  return el instanceof HTMLElement
}

/**
 * Check if given element is a valid DOM element
 * @param {object} el - Element to check
 * @returns {boolean} - true if it is a valid DOM element
 */
function isDOMElement(el: any): boolean {
  return (
    typeof el === 'object' &&
    el.nodeType === 1 &&
    typeof el.nodeName === 'string'
  )
}

/**
 * Check if given element is a valid jQuery object
 * @param {object} el - Element to check
 * @returns {boolean} - true if it is a valid jQuery object
 */
function isjQueryElement(el: any): boolean {
  return el && typeof el.jquery !== 'undefined'
}

/**
 * Check if given element is a valid HTML node
 * @param {object} el - Element to check
 * @returns {boolean} - true if it is a valid HTML node
 */
function isHTMLNode(el: any): boolean {
  return typeof Node === 'function'
    ? el instanceof Node
    : el &&
    typeof el === 'object' &&
    typeof el.nodeType === 'number' &&
    typeof el.nodeName === 'string'
}

/**
 * Check if given element is a valid DOM element or jQuery object
 * and returns a DOM element.
 *
 * @param {object} el - Element to check
 * @returns {object} - DOM element
 * @throws {Error} - Throws an error if the element is not a valid DOM element or jQuery object
 */
function toDOMElement(el: any): object {
  if (!isDOMElement(el) && !isjQueryElement(el)) {
    throw new Error('Element is not a valid DOM element or jQuery object!')
  }

  if (!isDOMElement(el)) {
    return el[0]
  }

  return el
}

/**
 * Liefert den Wert eines QueryParameters anhand des name aus der übergebenen url
 * @param url - url
 * @param name - Name des Queryparameters
 * @returns - Wert des QueryParameters (name)
 */
function getQueryParameter(url: string, name: string): string {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
  let regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
  let results = regex.exec(url)
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

/**
 * Ersetzt in einem url einen URL-Parameter(paramName) durch einen bestimmten Wert(paramValue)
 * sofern der Url-Parameter in diesem url existiert
 * @param url - url
 * @param paramName - name des Queryparameters
 * @param paramValue - wert des Queryparameters
 * @returns
 */
function replaceUrlParam(url: string, paramName: string, paramValue: string): string {
  const pattern = new RegExp('(\\?|\\&)(' + paramName + '=).*?(&|$)')
  let newUrl = url
  if (url.search(pattern) >= 0) {
    newUrl = url.replace(pattern, '$1$2' + paramValue + '$3')
  }
  return newUrl
}

/**
 * Pause für ... millisekunden
 * @param ms
 * @returns
 */
function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Helper class to render classes correctly (undefined gets mapped into an empty string)
 */
function getClassNames(classNames: string[]) {
  return classNames.join(' ')
}

/**
 * Some sitefinity attributes are being delivered as strings in a key value shape, eg. "rel='nofollow'".
 * The function extracts the value part in order for it to be assigned in the TSX component
 * @param attribute string
 */
function getSitefinityValue(attribute: string) {
  return attribute?.split('=')[1]
      ?.replace(/"/g, '')
      ?.replace(/'/g, '')
}

export {
  getDimensions,
  getHiddenDimensions,
  getExpandedDimensions,
  getUnExpandedDimensions,
  camelCaseSelector,
  capitalizeFirstLetter,
  secondsToMS,
  qualifyURL,
  isHTMLElement,
  isDOMElement,
  isjQueryElement,
  isHTMLNode,
  toDOMElement,
  getQueryParameter,
  replaceUrlParam,
  sleep,
  getClassNames,
  getSitefinityValue
}
